import React from "react";
import { FlaskConical, BookOpenCheck, Table2 } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";

const METHOD = [
    "A literature review of peer-reviewed and official sources on FRT accuracy, surveillance and law, prioritising empirical studies (NIST, 2019) and landmark audits (Buolamwini & Gebru, 2018).",
    "Primary policy documents: the IPCA's investigation into police emergent technologies (2020), the Privacy Commissioner's Biometric Processing Privacy Code consultation paper (2024) and FRT inquiry report (2025).",
    "Contemporary NZ journalism (RNZ, NZ Herald) used only where official documents were unavailable, and always as a secondary source.",
    "Inclusion criteria: each claim must trace to an identifiable source; each source must be cited in APA 7 style on the References page; unsupported claims were cut.",
];

const FINDINGS = [
    {
        no: "F1",
        title: "New Zealand has no biometric-specific law",
        body: "Police FRT is governed only by the general Privacy Act 2020 and police internal policy — self-regulation that a future administration could reverse without legislative scrutiny (Office of the Privacy Commissioner, 2024; Radio New Zealand, 2024).",
    },
    {
        no: "F2",
        title: "Deployment has repeatedly outrun governance",
        body: "The 2018 proof-of-concept proceeded without a privacy impact assessment; the 2020 Clearview AI trial ran without clearance from Police executive or the Privacy Commissioner. Both were documented only after the fact (Independent Police Conduct Authority, 2020).",
    },
    {
        no: "F3",
        title: "Error rates are demographically skewed",
        body: "False positives concentrate in identifiable groups: up to 100× differential false-positive rates across demographics in NIST testing (Grother et al., 2019), and 0.8% vs 34.7% error across intersectional groups in landmark audits (Buolamwini & Gebru, 2018). The OPC flagged the NZ-specific risk of overseas-trained datasets for Māori and Pacific people (2025).",
    },
    {
        no: "F4",
        title: "Thresholds and verification decide real-world harm",
        body: "In the only NZ system formally examined, two misidentifications forced the intervention threshold from 90% to 92.5%, and two-staff verification caught errors before action followed — evidence that governance settings, not raw capability, determine outcomes (Office of the Privacy Commissioner, 2025).",
    },
    {
        no: "F5",
        title: "Each governance option maps to a working precedent",
        body: "Human verification is proven in the NZ retail trial (2025); independent oversight is the direction of the OPC's draft Code (2024) and the lesson of Bridges v South Wales Police (2020); temporary restriction is the settled practice of jurisdictions from San Francisco (2019) to the European Parliament's moratorium calls.",
    },
];

export default function OurResearch() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-08"
                lines={["Our research", <>& <i className="text-amber">findings</i></>]}
                lede="This dossier is the product of a structured review: what reliable sources establish about FRT, what New Zealand's own record documents, and what follows when the two are put side by side. Five findings summarise the result."
                meta={["SUBJECT: EVIDENCE SYNTHESIS — LEAD: MOELILI'A"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div>
                        <SectionHead
                            no="8.1"
                            kicker="Method"
                            title="How we researched this"
                            lede="Four rules governed the work:"
                        />
                        <div className="space-y-px bg-white/10 border border-white/10">
                            {METHOD.map((m, i) => (
                                <Reveal key={i} delay={i * 0.05} className="bg-ink">
                                    <div className="flex gap-5 p-6" data-testid={`research-method-${i + 1}`}>
                                        <FlaskConical size={16} className="text-amber shrink-0 mt-1" />
                                        <p className="text-sm leading-relaxed text-ash">{m}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div className="lg:pt-4">
                        <FigureBlock
                            num="8"
                            src="https://images.unsplash.com/photo-1585206031650-9e9a7c87dcfe?q=80&w=1600&auto=format&fit=crop"
                            alt="Hardware and wiring connected to computing infrastructure"
                            caption="Behind every governance debate sits physical infrastructure: storage, compute and interfaces that hold biometric data whether or not any rules govern it."
                            ratio="4/3"
                        />
                        <Reveal delay={0.12} className="mt-8">
                            <div className="border border-white/10 p-6">
                                <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-3">
                                    <BookOpenCheck size={14} /> Source hierarchy
                                </p>
                                <p className="text-sm leading-relaxed text-ash">
                                    Peer-reviewed studies → official reports &amp; inquiries → legislation &amp; case law →
                                    journalism. At least ten academic and official sources anchor this dossier; the full
                                    hierarchy is visible on the References page.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="8.2"
                        kicker="Findings"
                        title="Five findings from the evidence"
                        lede="Each finding is stated with its supporting evidence and cited in full on the References page."
                    />
                    <div className="space-y-px bg-white/10 border border-white/10">
                        {FINDINGS.map((f, i) => (
                            <Reveal key={f.no} delay={i * 0.05} className="bg-ink">
                                <div className="p-7 sm:p-9" data-testid={`research-finding-${f.no.toLowerCase()}`}>
                                    <div className="flex flex-col sm:flex-row gap-5 sm:gap-10">
                                        <span className="font-display text-stroke text-5xl font-semibold leading-none shrink-0">
                                            {f.no}
                                        </span>
                                        <div>
                                            <h3 className="font-display text-xl sm:text-2xl text-paper mb-3">{f.title}</h3>
                                            <p className="text-sm sm:text-base leading-relaxed text-ash max-w-4xl">{f.body}</p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1} className="mt-12">
                        <div className="flex items-start gap-4 border border-amber/30 bg-amber/[0.05] p-7">
                            <Table2 size={18} className="text-amber shrink-0 mt-1" />
                            <p className="text-sm leading-relaxed text-paper/85">
                                Taken together, the findings support one conclusion: the technology's trajectory is not
                                waiting for New Zealand's law to catch up — so the choice between{" "}
                                <span className="text-amber">verification, oversight and restriction</span> is the
                                decisive question, which is why this dossier devotes its largest section to it.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
